import type { ChangeEvent } from 'react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { Company } from 'src/types';

interface CompanyFormProps {
  initialData?: Company;
  onSubmit: (data: Partial<Company>) => void;
  isLoading: boolean;
  onCancel: () => void;
}

const CompanyForm = ({ initialData, onSubmit, isLoading, onCancel }: CompanyFormProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: initialData?.name ?? '',
    address: initialData?.address ?? '',
    description: initialData?.description ?? '',
    contact_email: initialData?.contact_email ?? '',
    contact_phone: initialData?.contact_phone ?? '',
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const data: Partial<Company> = {};
    for (const [key, value] of Object.entries(formData)) {
      if (value !== '') {
        (data as Record<string, string>)[key] = value;
      }
    }
    onSubmit(data);
  };

  return (
    <form className="management-form" onSubmit={handleSubmit}>
      <div className="management-form-field">
        <label htmlFor="company-name">{t('management.company.name')} *</label>
        <input
          id="company-name"
          name="name"
          type="text"
          value={formData.name}
          onChange={handleChange}
          required
        />
      </div>
      <div className="management-form-field">
        <label htmlFor="company-address">{t('management.company.address')}</label>
        <input
          id="company-address"
          name="address"
          type="text"
          value={formData.address}
          onChange={handleChange}
        />
      </div>
      <div className="management-form-field">
        <label htmlFor="company-description">{t('management.company.description')}</label>
        <textarea
          id="company-description"
          name="description"
          value={formData.description}
          onChange={handleChange}
        />
      </div>
      <div className="management-form-row">
        <div className="management-form-field">
          <label htmlFor="company-email">{t('management.company.contactEmail')}</label>
          <input
            id="company-email"
            name="contact_email"
            type="email"
            value={formData.contact_email}
            onChange={handleChange}
          />
        </div>
        <div className="management-form-field">
          <label htmlFor="company-phone">{t('management.company.contactPhone')}</label>
          <input
            id="company-phone"
            name="contact_phone"
            type="tel"
            value={formData.contact_phone}
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="entity-modal-footer" style={{ padding: 0, border: 'none' }}>
        <button
          type="button"
          className="management-btn management-btn--secondary"
          onClick={onCancel}
        >
          {t('management.cancel')}
        </button>
        <button
          type="submit"
          className="management-btn management-btn--primary"
          disabled={isLoading}
        >
          {isLoading ? t('management.saving') : t('management.save')}
        </button>
      </div>
    </form>
  );
};

export default CompanyForm;
